import {
  Company,
  IAsset,
  IDailyContentPlaylist,
  ISignagePlaylist,
  ISignagePromotion,
  NendaProduct,
} from "../../types/NendaTypes";
import {
  GroupProps,
  IAssetListRequest,
  ICompanyListRequest,
} from "../../types/RequestTypes";
import { httpClient, Service } from "./client";

interface UpdateCompanyAssetProps {
  companyId: string;
  assetId: string;
  product: NendaProduct;
  asset: Partial<IAsset>;
}
class CompanyService extends Service {
  getCompany(companyId: string) {
    return httpClient.get(`/company/${companyId}`, this.authHeader());
  }
  getCompanies(listRequest?: ICompanyListRequest) {
    const query = httpClient.listRequestToQuery(listRequest);

    return httpClient.get("/company" + query, this.authHeader());
  }
  createCompany(company: Company) {
    return httpClient.post("/company", company, this.authHeader());
  }
  updateCompany(companyId: string, company: Company) {
    return httpClient.put(`/company/${companyId}`, company, this.authHeader());
  }
  deleteCompany(companyId: string) {
    return httpClient.delete(`/company/${companyId}`, this.authHeader());
  }
  getUsers(companyId: string) {
    return httpClient.get(`/company/${companyId}/users`, this.authHeader());
  }
  getScreens(companyId: string) {
    return httpClient.get(`/company/${companyId}/screen`, this.authHeader());
  }
  getAssets(companyId: string, listRequest?: IAssetListRequest) {
    const query = httpClient.listRequestToQuery(listRequest);
    return httpClient.get(
      `/company/${companyId}/asset` + query,
      this.authHeader()
    );
  }
  createAsset(companyId: string, asset: IAsset) {
    return httpClient.post(
      `/company/${companyId}/asset`,
      { ...asset },
      this.authHeader()
    );
  }
  /**
   * Checks whether a URL can be embedded in an iframe and captures a preview
   * screenshot for it. Used for both company and premise assets, since the
   * check itself does not depend on the scope.
   */
  validateAssetUrl(companyId: string, url: string) {
    return httpClient.post(
      `/company/${companyId}/asset/validate-url`,
      { url },
      this.authHeader()
    );
  }
  updateAsset({ companyId, assetId, product, asset }: UpdateCompanyAssetProps) {
    return httpClient.patch(
      `/company/${companyId}/asset/${product}/${assetId}`,
      { asset },
      this.authHeader()
    );
  }
  deleteAsset(companyId: string, product: NendaProduct, assetId: string) {
    return httpClient.delete(
      `/company/${companyId}/asset/${product}/${assetId}`,
      this.authHeader()
    );
  }

  uploadMediaFile(
    companyId: string,
    file: Blob,
    scope: string,
    assetType: string
  ) {
    const formData = new FormData();
    formData.append("scope", scope);
    formData.append("assetType", assetType);
    formData.append("file", file);

    return httpClient.put(
      `/company/${companyId}/file`,
      formData,
      this.authHeader(),
      false
    );
  }

  getPromotions(companyId: string) {
    return httpClient.get(
      `/company/${companyId}/signagepromotion`,
      this.authHeader()
    );
  }

  getPromotion(companyId: string, promotionId: string) {
    return httpClient.get(
      `/company/${companyId}/signagepromotion/${promotionId}`,
      this.authHeader()
    );
  }

  createPromotion(
    companyId: string,
    promotion: ISignagePromotion,
    applyToScreens: string[]
  ) {
    return httpClient.post(
      `/company/${companyId}/signagepromotion`,
      { promotion, applyToScreens },
      this.authHeader()
    );
  }

  deletePromotion(companyId: string, promotionId: string) {
    return httpClient.delete(
      `/company/${companyId}/signagepromotion/${promotionId}`,
      this.authHeader()
    );
  }
  updatePromotion(
    companyId: string,
    promotionId: string,
    promotionData: Partial<ISignagePromotion>,
    applyToScreens: string[]
  ) {
    return httpClient.patch(
      `/company/${companyId}/signagepromotion/${promotionId}`,
      { promotionData, applyToScreens },
      this.authHeader()
    );
  }

  getSignagePlaylists(companyId: string) {
    return httpClient.get(
      `/company/${companyId}/signageplaylist`,
      this.authHeader()
    );
  }

  updateSignagePlaylist(playlist: ISignagePlaylist) {
    return httpClient.patch(
      `/company/${playlist.company}/signageplaylist/${playlist._id}`,
      playlist,
      this.authHeader()
    );
  }
  createSignagePlaylist(playlist: ISignagePlaylist) {
    return httpClient.post(
      `/company/${playlist.company}/signageplaylist`,
      playlist,
      this.authHeader()
    );
  }
  deleteSignagePlaylist(companyId: string, playlistId: string) {
    return httpClient.delete(
      `/company/${companyId}/signageplaylist/${playlistId}`,
      this.authHeader()
    );
  }
  getGroup(companyId: string, groupId: string) {
    return httpClient.get(
      `/company/${companyId}/group/${groupId}`,
      this.authHeader()
    );
  }

  getGroups(companyId: string) {
    return httpClient.get(`/company/${companyId}/group`, this.authHeader());
  }
  createGroup(companyId: string, group: GroupProps) {
    return httpClient.post(
      `/company/${companyId}/group`,
      group,
      this.authHeader()
    );
  }
  updateGroup(companyId: string, groupId: string, group: Partial<GroupProps>) {
    return httpClient.patch(
      `/company/${companyId}/group/${groupId}`,
      { group },
      this.authHeader()
    );
  }
  deleteGroup(companyId: string, groupId: string) {
    return httpClient.delete(
      `/company/${companyId}/group/${groupId}`,
      this.authHeader()
    );
  }
  getDailyContentPlaylists(companyId: string) {
    return httpClient.get(
      `/company/${companyId}/dailycontentplaylist`,
      this.authHeader()
    );
  }
  getDailyContentPlaylist(companyId: string, playlistId: string) {
    return httpClient.get(
      `/company/${companyId}/dailycontentplaylist/${playlistId}`,
      this.authHeader()
    );
  }
  createDailyContentPlaylist(
    companyId: string,
    playlist: IDailyContentPlaylist
  ) {
    return httpClient.post(
      `/company/${companyId}/dailycontentplaylist`,
      playlist,
      this.authHeader()
    );
  }
  updateDailyContentPlaylist(playlist: Partial<IDailyContentPlaylist>) {
    return httpClient.patch(
      `/company/${playlist.company}/dailycontentplaylist/${playlist._id}`,
      playlist,
      this.authHeader()
    );
  }
  deleteDailyContentPlaylist(companyId: string, playlistId: string) {
    return httpClient.delete(
      `/company/${companyId}/dailycontentplaylist/${playlistId}`,
      this.authHeader()
    );
  }
}

export const companyService = new CompanyService();
